<template>
  <div class="content-wrapper">
    <div class="content-body">

      <!-- ACCESS GUARD -->
      <div
        v-if="!$can('CHANTIER:admin')"
        class="chantiers-admin-empty chantiers-admin-empty--error"
      >
        <feather-icon
          icon="LockIcon"
          size="32"
        />
        <p class="mt-1 mb-0">
          Vous n'avez pas les droits pour accéder à cette page.
        </p>
      </div>

      <template v-else>
        <!-- TOOLBAR -->
        <div class="chantiers-admin-toolbar">
          <div class="chantiers-admin-toolbar__title">
            <feather-icon
              icon="TagIcon"
              size="18"
              class="mr-50"
            />
            <strong>Catégories de chantier</strong>
          </div>
          <b-button
            variant="primary"
            @click="openCreate"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
              class="mr-50"
            />
            Nouvelle catégorie
          </b-button>
        </div>

        <!-- LOADING -->
        <div
          v-if="loading"
          class="chantiers-admin-empty"
        >
          <b-spinner class="mb-1" />
          <p class="mt-1 mb-0">
            Chargement des catégories…
          </p>
        </div>

        <!-- ERROR -->
        <div
          v-else-if="error"
          class="chantiers-admin-empty chantiers-admin-empty--error"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            size="32"
          />
          <p class="mt-1 mb-1">
            {{ error }}
          </p>
          <b-button
            variant="outline-primary"
            size="sm"
            @click="loadCategories"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              class="mr-50"
            />
            Réessayer
          </b-button>
        </div>

        <!-- EMPTY -->
        <div
          v-else-if="categories.length === 0"
          class="chantiers-admin-empty"
        >
          <feather-icon
            icon="InboxIcon"
            size="32"
          />
          <p class="mt-1 mb-0">
            Aucune catégorie pour le moment.
          </p>
        </div>

        <!-- TABLE -->
        <div
          v-else
          class="chantiers-admin-card"
        >
          <b-table
            :items="categories"
            :fields="fields"
            responsive
            hover
            class="mb-0"
          >
            <template #cell(couleur)="data">
              <span
                class="chantiers-admin-swatch"
                :style="{ backgroundColor: data.item.couleur || '#5A7373' }"
              />
              <span class="ml-50">{{ data.item.couleur || '—' }}</span>
            </template>
            <template #cell(actions)="data">
              <b-button
                variant="flat-secondary"
                size="sm"
                @click="openEdit(data.item)"
              >
                <feather-icon
                  icon="Edit2Icon"
                  size="14"
                />
              </b-button>
              <b-button
                variant="flat-danger"
                size="sm"
                @click="confirmDelete(data.item)"
              >
                <feather-icon
                  icon="Trash2Icon"
                  size="14"
                />
              </b-button>
            </template>
          </b-table>
        </div>
      </template>

      <!-- CREATE / EDIT MODAL -->
      <b-modal
        id="categorie-modal"
        :title="editing ? 'Modifier la catégorie' : 'Nouvelle catégorie'"
        :ok-title="editing ? 'Enregistrer' : 'Créer'"
        cancel-title="Annuler"
        cancel-variant="outline-secondary"
        :ok-disabled="saving || !form.libelle"
        :busy="saving"
        @ok.prevent="submit"
      >
        <b-form-group
          label="Libellé"
          label-for="cat-libelle"
        >
          <b-form-input
            id="cat-libelle"
            v-model="form.libelle"
            placeholder="Ex. ISO 9001"
            :disabled="saving"
          />
        </b-form-group>
        <b-form-group
          label="Couleur"
          label-for="cat-couleur"
        >
          <div class="d-flex align-items-center">
            <b-form-input
              id="cat-couleur"
              v-model="form.couleur"
              type="color"
              class="chantiers-admin-color"
              :disabled="saving"
            />
            <b-form-input
              v-model="form.couleur"
              placeholder="#1c4d4d"
              class="ml-1"
              :disabled="saving"
            />
          </div>
        </b-form-group>
      </b-modal>

    </div>
  </div>
</template>

<script>
import {
  BButton,
  BFormGroup,
  BFormInput,
  BModal,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import { successAlert, errorAlert } from '@/libs/sweetAlerts/alerts'

export default {
  name: 'CategoriesChantierAdmin',
  components: {
    BButton,
    BFormGroup,
    BFormInput,
    BModal,
    BSpinner,
    BTable,
  },
  data() {
    return {
      categories: [],
      loading: true,
      error: null,
      saving: false,
      editing: null,
      form: { libelle: '', couleur: '#1c4d4d' },
      fields: [
        { key: 'libelle', label: 'Libellé' },
        { key: 'couleur', label: 'Couleur' },
        { key: 'actions', label: 'Actions', class: 'text-right' },
      ],
    }
  },
  created() {
    if (this.$can('CHANTIER:admin')) {
      this.loadCategories()
    } else {
      this.loading = false
    }
  },
  methods: {
    async loadCategories() {
      this.loading = true
      this.error = null
      try {
        const response = await this.$http.myressif.chantiers.listCategories()
        this.categories = (response && response.items) || []
      } catch (e) {
        this.categories = []
        this.error = 'Une erreur est survenue lors du chargement des catégories.'
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.editing = null
      this.form = { libelle: '', couleur: '#1c4d4d' }
      this.$bvModal.show('categorie-modal')
    },
    openEdit(item) {
      this.editing = item
      this.form = { libelle: item.libelle, couleur: item.couleur || '#1c4d4d' }
      this.$bvModal.show('categorie-modal')
    },
    async submit() {
      if (!this.form.libelle) return
      this.saving = true
      try {
        const payload = { libelle: this.form.libelle, couleur: this.form.couleur }
        if (this.editing) {
          await this.$http.myressif.chantiers.updateCategorie(this.editing.id, payload)
        } else {
          await this.$http.myressif.chantiers.createCategorie(payload)
        }
        this.$bvModal.hide('categorie-modal')
        successAlert.fire({
          title: this.editing ? 'Catégorie modifiée' : 'Catégorie créée',
          text: 'Opération effectuée avec succès.',
        })
        await this.loadCategories()
      } catch (e) {
        errorAlert.fire({
          title: 'Oops',
          text: 'L\'enregistrement de la catégorie a échoué.',
        })
      } finally {
        this.saving = false
      }
    },
    confirmDelete(item) {
      this.$bvModal
        .msgBoxConfirm(`Êtes-vous sûr de vouloir supprimer la catégorie « ${item.libelle} » ?`, {
          title: 'Confirmation de suppression',
          size: 'sm',
          okVariant: 'danger',
          okTitle: 'Supprimer',
          cancelTitle: 'Annuler',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        .then(value => {
          if (value === true) this.doDelete(item)
        })
    },
    async doDelete(item) {
      try {
        await this.$http.myressif.chantiers.deleteCategorie(item.id)
        successAlert.fire({
          title: 'Catégorie supprimée',
          text: 'Suppression effectuée avec succès.',
        })
        await this.loadCategories()
      } catch (e) {
        errorAlert.fire({
          title: 'Oops',
          text: 'La suppression de la catégorie a échoué.',
        })
      }
    },
  },
}
</script>

<style scoped lang="scss">
$at-green:   #1c4d4d;
$at-orange:  #ff6131;
$at-ink-500: #5A7373;
$at-ink-100: #E4ECEC;

.chantiers-admin-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  padding: 14px 18px;
  margin-bottom: 16px;

  &__title {
    display: inline-flex;
    align-items: center;
    color: $at-green;
    font-size: 16px;
  }
}

.chantiers-admin-card {
  background: #fff;
  border: 1px solid $at-ink-100;
  border-radius: 10px;
  overflow: hidden;
}

.chantiers-admin-empty {
  text-align: center;
  padding: 48px 16px;
  color: $at-ink-500;
  background: #fff;
  border: 1px dashed $at-ink-100;
  border-radius: 10px;

  &--error {
    color: $at-orange;
    border-color: rgba(255, 97, 49, .3);
  }
}

.chantiers-admin-swatch {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  vertical-align: middle;
  border: 1px solid $at-ink-100;
}

.chantiers-admin-color {
  width: 48px;
  padding: 2px;
}
</style>
